import GuardianConsentFields from './GuardianConsentFields';
/**
 * ADORACARE-PATIENT-MASTER-B2 — the 30-second front-desk patient create
 * (`/dashboard/patients/new`), recomposed by INTAKE-V2-BUILD-V1 to host
 * THE ONE reusable register field set (RegisterPatientSteps.tsx) — the
 * same component the arrival wizard renders, so no second registration
 * surface can drift (brief must-match 13; C13 SEARCH FIRST: this page is
 * reachable only through the patient-search evidence doors -- ENFORCED
 * MECHANICALLY by the search-origin marker gate below, not by this
 * comment: direct navigation without a valid `from=` marker redirects to
 * patient search; the PR #414 founder corrective).
 *
 * FOUNDER RULING D7 (2026-07-31): opened from search with no appointment
 * in hand, this door registers the PERSON only — it lands on the new
 * patient record and creates NO visit. A visit is started explicitly only
 * when one is real. (The appointment-anchored path stays the arrival
 * wizard's; this file calls no appointment or visit writer.)
 *
 * Requiredness renders from the SAME org config the wizard reads
 * (GET /api/registration-config; must-match 7 — zero tenant field lists in
 * code); a failed config read degrades to the LAW trio and never blocks
 * registration (D3 direction).
 *
 * UMR is issued SERVER-side on save (DB trigger) — never typed, never
 * client-generated. MINOR RULE (B0 front-desk flow 3): when the entered
 * age/dob says minor, the guardian + consent section appears and is
 * REQUIRED — the client validates first for fast feedback, and the server
 * enforces it for real.
 *
 * Outcomes:
 *   created          → navigate to the new patient record (UMR shown
 *                      there); D7's landing.
 *   duplicate_match  → the server found the same phone+name+dob already
 *                      registered (deterministic ladder rung) — the
 *                      existing record is offered instead; not an error.
 *
 * `?q=` prefill: a phone-shaped search query lands in the mobile field;
 * anything else lands in the name field.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { createPatientMasterPatient, fetchRegistrationConfig } from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  buildCreatePayload,
  createFormAddressDefaultsPatch,
  EMPTY_CREATE_FORM,
  genderAgeLabel,
  isPlausibleMobile,
  missingRequiredCreateKeys,
  parseRegisterOrigin,
  patientRecordHref,
  registrationConfigErrors,
  validateCreateForm,
  type CreateFormState,
  type CreatePatientResponse,
} from '@/dashboard/patients/patient-master-view';
import { registrationNeedsLine } from '@/dashboard/appointments/registration-field-plan';
import {
  ARRIVAL_SEARCH_UNAVAILABLE,
  EMPTY_ARRIVAL_FORM,
  FALLBACK_REGISTRATION_CONFIG,
  patientPlaneRequiredFields,
  registrationSectionsPrefill,
  resolvedRequiredFields,
  type ArrivalFieldError,
  type ArrivalWizardForm,
  type RegistrationConfigView,
  type RegistrationRequiredField,
  type RegistrationVocabularyPrefill,
} from '@/dashboard/patients/register-wizard-model';
import { RegisterDetailsFields } from '@/dashboard/patients/RegisterPatientSteps';
import { AcButton } from '@/dashboard/ui';

export interface PatientCreateContentProps {
  readonly visitFields?: React.ReactNode;
  readonly form: CreateFormState;
  readonly onFieldChange?: (field: keyof CreateFormState, value: string) => void;
  readonly onSubmit?: () => void;
  readonly submitting: boolean;
  readonly error: string | null;
  /** Field-keyed inline validation messages (shown after first submit). */
  readonly fieldErrors: Readonly<Record<string, string>>;
  /** True when the entered age/dob says minor → guardian section shows. */
  readonly isMinor: boolean;
  /** Set when the server returned duplicate_match. */
  readonly duplicate: Extract<
    CreatePatientResponse,
    { outcome: 'duplicate_match' }
  > | null;
  /** The resolved org requiredness (stars); defaults to the LAW trio. */
  readonly required?: ReadonlySet<RegistrationRequiredField>;
  /** RECEPTION-OS-BUILD-V1 (Q-09=D): the org's handling-category tag
   *  vocabulary; absent/[] renders no tag select. */
  readonly handlingCategories?: readonly string[];
  /**
   * FRONT-OFFICE-OS-V2-BUILD-V1 (FO-5): 'modal' hosts THE SAME content
   * inside the centered register modal -- the S-2 title lives in the modal
   * head (no inline h1) and the ONE primary submit lives in the modal
   * footer (no inline button; Enter still submits through the form).
   * DEFAULT 'page' renders byte-identically to the pre-package page --
   * every existing pin unchanged (the DrawerShell optional-control
   * precedent). ONE field set, ONE writer, now three hosts.
   */
  readonly variant?: 'page' | 'modal';
  readonly workspaceRegistration?: boolean;
  /** FD-16 continue-from-enquiry (UX LAW 1): replaces the mobile input
   *  with the on-file confirmation line (the bridge copies the phone
   *  server-side). Absent = unchanged. */
  readonly mobileOnFile?: string | null;
  /** REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (Defect C parity + B3): the compact
   *  "Registration still needs: X, and N more." line, shown after a submit
   *  attempt when required contract fields are empty -- the SAME desk voice +
   *  compact grammar the check-in door's needs-line uses. Null = nothing to
   *  say (the field asterisks are the primary truth). */
  readonly needsLine?: string | null;
  /** REGISTRATION-FIELD-CONTROLS-V1: the governed picklist vocabularies the
   *  shared module renders (title / relation / relation-title / occupation +
   *  the district cascade). Absent = every picklist degrades to free text
   *  (R-017) -- the pre-package behavior. */
  readonly sectionsPrefill?: RegistrationVocabularyPrefill;
}


/** Adapt the page's CreateFormState + field-keyed errors onto THE shared
 *  register field set (one presentation, two hosts — never a fork). */
function toSharedForm(
  form: CreateFormState,
  ageMode: 'age' | 'dob',
  addressExpanded: boolean,
): ArrivalWizardForm {
  return {
    ...EMPTY_ARRIVAL_FORM,
    contactReuse: form.contactReuse,
    fullName: form.fullName,
    mobile: form.mobile,
    ageMode,
    ageYears: form.ageYears,
    dob: form.dob,
    gender: form.gender === 'unknown' ? '' : form.gender,
    city: form.city,
    complaint: '',
    alertFlag: false,
    consentPath: '',
    guardianName: form.guardianName,
    guardianRelationship: form.guardianRelationship,
    relationName: form.relationName,
    occupation: form.occupation,
    handlingCategory: form.handlingCategory,
    relationPrefix: form.relationPrefix,
    externalMr: form.externalMr,
    nameTitle: form.nameTitle,
    lastName: form.lastName,
    relationTitle: form.relationTitle,
    // ROUND-4 ACCEPTANCE CORRECTIVE (founder + Dr. Lakshmi walk 2026-08-15):
    // the G2 email was in CreateFormState, the validator, and the payload --
    // but this host never DISPLAYED it (here) nor FORWARDED its patch (the
    // onChange mapper below), so the desk doors' email field ate every
    // keystroke: the controlled input snapped back to the unchanged ''.
    email: form.email,
    altMobile: form.altMobile,
    altMobileRelation: form.altMobileRelation,
    altMobileRelationName: form.altMobileRelationName,
    state: form.state,
    pincode: form.pincode,
    addressArea: form.addressArea,
    addressDoor: form.addressDoor,
    addressStreet: form.addressStreet,
    addressLandmark: form.addressLandmark,
    addressTaluka: form.addressTaluka,
    addressCountry: form.addressCountry,
    addressExpanded,
    emergencyContactName: form.emergencyContactName,
    emergencyContactRelation: form.emergencyContactRelation,
    emergencyContactPhone: form.emergencyContactPhone,
  };
}

function toSharedErrors(
  fieldErrors: Readonly<Record<string, string>>,
): readonly ArrivalFieldError[] {
  return Object.entries(fieldErrors)
    .filter(([field]) =>
      [
        'fullName',
        'mobile',
        'ageYears',
        'dob',
        'gender',
        'city',
        'relationName',
        'occupation',
      ].includes(field),
    )
    .map(([field, message]) => ({ field, message, footer: message }));
}

/**
 * Pure presentational body (no fetch) — rendered directly in tests via
 * react-dom/server (wrapped in a Router for links).
 */
export function PatientCreateContent({
  visitFields,
  form,
  onFieldChange,
  onSubmit,
  submitting,
  error,
  fieldErrors,
  isMinor,
  duplicate,
  required,
  handlingCategories,
  variant = 'page',
  workspaceRegistration = false,
  mobileOnFile = null,
  needsLine = null,
  sectionsPrefill,
}: PatientCreateContentProps): JSX.Element {
  // The age-or-DOB toggle is presentation state only: both values live in
  // CreateFormState and switching modes never wipes the other (the wizard's
  // recovery-matrix rule carried here).
  const [ageMode, setAgeMode] = useState<'age' | 'dob'>(
    form.dob.trim() !== '' ? 'dob' : 'age',
  );
  // ONE-REGISTRATION-MODULE: the optional full-address expander is a UI-only
  // toggle for this host (like ageMode), never a persisted form field.
  const [addressExpanded, setAddressExpanded] = useState(false);
  const requiredSet =
    required ?? resolvedRequiredFields(FALLBACK_REGISTRATION_CONFIG);


  return (
    <section
      {...(variant === 'page'
        ? { 'aria-labelledby': 'patient-create-heading' }
        : { 'aria-label': 'Register patient' })}
    >
      {variant === 'page' ? (
        <h1 id="patient-create-heading">Register new patient</h1>
      ) : null}
      {workspaceRegistration ? null : <p
        style={{
          margin: 0,
          fontSize: 'var(--ac-text-detail)',
          color: 'hsl(var(--ac-ink-subtle))',
        }}
      >
        The UMR is issued automatically on save. Shared family phone
        numbers are supported — one number may belong to several patients.
      </p>}

      {duplicate !== null ? (
        <div className="ac-banner ac-banner--info" role="status">
          <p>
            <strong>This patient may already be registered.</strong> A
            patient with the same mobile, name, and date of birth already
            exists. Open the existing record instead of creating a
            duplicate:
          </p>
          <ul>
            {duplicate.candidates.map((c) => (
              <li key={c.patientId}>
                <Link
                  to={patientRecordHref(c.patientId)}
                  className="ac-wizard__quiet"
                >
                  {c.fullName}
                </Link>{' '}
                {genderAgeLabel(c.gender, c.ageYears)} · {c.umr}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <form
        className="ac-wizard ac-wizard__fields"
        style={{ marginTop: 'var(--ac-space-4)' }}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.();
        }}
      >
        <RegisterDetailsFields
          visitFields={visitFields}
          workspaceRegistration={workspaceRegistration}
          choicePresentation={variant === 'modal' ? 'select' : 'chips'}
          form={toSharedForm(form, ageMode, addressExpanded)}
          onChange={(patch) => {
            if (patch.contactReuse !== undefined) onFieldChange?.('contactReuse', patch.contactReuse);
            if (patch.ageMode !== undefined) setAgeMode(patch.ageMode);
            if (patch.fullName !== undefined) onFieldChange?.('fullName', patch.fullName);
            if (patch.mobile !== undefined) onFieldChange?.('mobile', patch.mobile);
            if (patch.ageYears !== undefined) onFieldChange?.('ageYears', patch.ageYears);
            if (patch.dob !== undefined) onFieldChange?.('dob', patch.dob);
            if (patch.gender !== undefined) onFieldChange?.('gender', patch.gender);
            if (patch.city !== undefined) onFieldChange?.('city', patch.city);
            if (patch.relationName !== undefined) {
              onFieldChange?.('relationName', patch.relationName);
            }
            if (patch.occupation !== undefined) {
              onFieldChange?.('occupation', patch.occupation);
            }
            if (patch.handlingCategory !== undefined) {
              onFieldChange?.('handlingCategory', patch.handlingCategory);
            }
            if (patch.relationPrefix !== undefined) {
              onFieldChange?.('relationPrefix', patch.relationPrefix);
            }
            if (patch.externalMr !== undefined) {
              onFieldChange?.('externalMr', patch.externalMr);
            }
            // ONE-REGISTRATION-MODULE: persist the governed contract fields the
            // shared module emits back onto CreateFormState (the expander is a
            // UI-only local toggle).
            if (patch.nameTitle !== undefined) {
              onFieldChange?.('nameTitle', patch.nameTitle);
            }
            if (patch.lastName !== undefined) {
              onFieldChange?.('lastName', patch.lastName);
            }
            if (patch.relationTitle !== undefined) {
              onFieldChange?.('relationTitle', patch.relationTitle);
            }
            // ROUND-4 ACCEPTANCE CORRECTIVE: the ONE branch G2 missed on this
            // host -- without it every email keystroke died right here.
            if (patch.email !== undefined) {
              onFieldChange?.('email', patch.email);
            }
            if (patch.altMobile !== undefined) {
              onFieldChange?.('altMobile', patch.altMobile);
            }
            if (patch.altMobileRelation !== undefined) {
              onFieldChange?.('altMobileRelation', patch.altMobileRelation);
            }
            if (patch.altMobileRelationName !== undefined) {
              onFieldChange?.('altMobileRelationName', patch.altMobileRelationName);
            }
            if (patch.state !== undefined) onFieldChange?.('state', patch.state);
            if (patch.pincode !== undefined) {
              onFieldChange?.('pincode', patch.pincode);
            }
            if (patch.addressArea !== undefined) {
              onFieldChange?.('addressArea', patch.addressArea);
            }
            if (patch.addressDoor !== undefined) {
              onFieldChange?.('addressDoor', patch.addressDoor);
            }
            if (patch.addressStreet !== undefined) {
              onFieldChange?.('addressStreet', patch.addressStreet);
            }
            if (patch.addressLandmark !== undefined) {
              onFieldChange?.('addressLandmark', patch.addressLandmark);
            }
            if (patch.addressTaluka !== undefined) {
              onFieldChange?.('addressTaluka', patch.addressTaluka);
            }
            if (patch.addressCountry !== undefined) {
              onFieldChange?.('addressCountry', patch.addressCountry);
            }
            if (patch.addressExpanded !== undefined) {
              setAddressExpanded(patch.addressExpanded);
            }
            if (patch.emergencyContactName !== undefined) {
              onFieldChange?.('emergencyContactName', patch.emergencyContactName);
            }
            if (patch.emergencyContactRelation !== undefined) {
              onFieldChange?.(
                'emergencyContactRelation',
                patch.emergencyContactRelation,
              );
            }
            if (patch.emergencyContactPhone !== undefined) {
              onFieldChange?.('emergencyContactPhone', patch.emergencyContactPhone);
            }
          }}
          required={requiredSet}
          handlingCategories={handlingCategories ?? []}
          sectionsPrefill={sectionsPrefill}
          mobileOnFile={mobileOnFile}
          errors={toSharedErrors(fieldErrors)}
          busy={submitting}
          testIds={{
            name: 'patient-create-name',
            mobile: 'patient-create-mobile',
            ageToggle: 'patient-create-age-toggle',
            age: 'patient-create-age',
            dob: 'patient-create-dob',
            gender: 'patient-create-gender',
            city: 'patient-create-city',
          }}
        />

        {isMinor ? <GuardianConsentFields value={form} errors={fieldErrors}
          onChange={patch=>{for(const [key,value] of Object.entries(patch)) if(value!==undefined) onFieldChange?.(key as keyof CreateFormState,value);}} busy={submitting}/> : null}

        {needsLine ? (
          <div
            className="ac-banner ac-banner--danger"
            role="status"
            data-testid="patient-create-needs-line"
          >
            {needsLine}
          </div>
        ) : null}

        {error ? (
          <div className="ac-banner ac-banner--danger" role="alert">
            {error}
          </div>
        ) : null}

        {variant === 'page' ? (
          <div className="ac-wizard__foot">
            <AcButton
              type="submit"
              variant="primary"
              disabled={submitting}
              data-testid="patient-create-submit"
            >
              {submitting ? 'Saving...' : 'Register patient'}
            </AcButton>
          </div>
        ) : null}
      </form>
    </section>
  );
}

export default function PatientCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ctx = useDashboardMeContext();

  // SEARCH-FIRST STRUCTURAL GATE (PR #414 founder corrective): this page
  // renders a creation form ONLY behind a valid search-origin marker
  // (no-match / none-of-these / search-failed -- the doors the search
  // surface mints). Direct navigation without one redirects to patient
  // search instead of showing an immediate creation form. The marker is a
  // UI gate, never the duplicate-security boundary: the server-side
  // duplicate-first check (THE ONE writer's deterministic dedupe) remains
  // binding on every submit regardless of how this page was reached.
  const origin = parseRegisterOrigin(searchParams.get('from'));

  const prefill = (searchParams.get('q') ?? '').trim();
  const [form, setForm] = useState<CreateFormState>(() => ({
    ...EMPTY_CREATE_FORM,
    ...(prefill.length > 0
      ? isPlausibleMobile(prefill)
        ? { mobile: prefill }
        : { fullName: prefill }
      : {}),
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [duplicate, setDuplicate] = useState<Extract<
    CreatePatientResponse,
    { outcome: 'duplicate_match' }
  > | null>(null);
  const [orgConfig, setOrgConfig] = useState<RegistrationConfigView>(
    FALLBACK_REGISTRATION_CONFIG,
  );

  useEffect(() => {
    let cancelled = false;
    fetchRegistrationConfig()
      .then((c) => {
        if (!cancelled) {
          setOrgConfig(c);
          // REGISTRATION-FIELD-CONTROLS-V1: State/Country open PREFILLED
          // from the config defaults (contract section 9), still-empty
          // fields only -- typed values are never overwritten.
          setForm((prev) => ({ ...prev, ...createFormAddressDefaultsPatch(prev, c) }));
        }
      })
      .catch(() => {
        /* the LAW-trio fallback stands — a config fault never blocks */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Person-only door: gate + render ONLY the patients-plane-persistable contract
  // (attack-review Finding 1 -- never force a field the writer drops).
  const required = useMemo(
    () => patientPlaneRequiredFields(resolvedRequiredFields(orgConfig)),
    [orgConfig],
  );

  const nowMs = Date.now();
  const validation = useMemo(
    () => validateCreateForm(form, nowMs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form],
  );

  // The org's config-required fields join the page's validation (the same
  // resolved set the wizard enforces -- one requiredness truth per org;
  // RECEPTION-OS-BUILD-V1 folded the two inline copies into THE ONE
  // registrationConfigErrors helper).
  const configErrors = useMemo(
    () => registrationConfigErrors(form, required),
    [required, form],
  );

  const allErrors = useMemo(
    () => ({ ...configErrors, ...validation.errors }),
    [configErrors, validation.errors],
  );
  // REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (Defect C): the person-only door now
  // gates on the FULL org-required contract (the check-in door's requiredness),
  // not just the four fields registrationConfigErrors named -- so an operator
  // can no longer submit a Partha registration missing the Part C/D set. The
  // compact needs-line names the first gap + counts the rest (B3).
  const missingKeys = useMemo(
    () => missingRequiredCreateKeys(form, required),
    [form, required],
  );
  const formOk = validation.ok && missingKeys.length === 0;

  // Single-clinic operators register at their clinic automatically;
  // multi-clinic scope leaves the attribute null in v1 (named run-record
  // decision — registered_clinic_id is an attribute, never identity).
  const registeredClinicId =
    ctx?.me?.clinicIds.length === 1 ? ctx.me.clinicIds[0] ?? null : null;

  const submit = (): void => {
    setShowErrors(true);
    setDuplicate(null);
    if (!formOk || submitting) return;
    setSubmitting(true);
    setError(null);
    // D7: the person is registered; NO visit is created — the landing is
    // the patient record, where a visit can be started explicitly when
    // one is real.
    createPatientMasterPatient(
      buildCreatePayload(form, validation.isMinor, registeredClinicId),
    )
      .then((res) => {
        setSubmitting(false);
        if (res.outcome === 'duplicate_match') {
          setDuplicate(res);
          return;
        }
        navigate(patientRecordHref(res.patient.patientId), {
          replace: true,
        });
      })
      .catch((err: unknown) => {
        setSubmitting(false);
        setError(err instanceof Error ? err.message : 'Save failed');
      });
  };

  const onFieldChange = (field: keyof CreateFormState, value: string): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (origin === null) {
    // No valid search-origin marker -> back to the search-first door.
    return <Navigate to="/dashboard/patients" replace />;
  }

  return (
    <>
      {origin === 'search-failed' ? (
        // Degraded mode (d): the honest warning rides the form; the server
        // duplicate-first check stays mandatory at submit.
        <div
          className="ac-banner ac-banner--warn"
          role="status"
          data-testid="patient-create-degraded-note"
        >
          {ARRIVAL_SEARCH_UNAVAILABLE}
        </div>
      ) : null}
      <PatientCreateContent
        form={form}
        onFieldChange={onFieldChange}
        onSubmit={submit}
        submitting={submitting}
        error={error}
        fieldErrors={showErrors ? allErrors : {}}
        handlingCategories={orgConfig.handlingCategories}
        sectionsPrefill={registrationSectionsPrefill(orgConfig)}
        isMinor={validation.isMinor}
        duplicate={duplicate}
        required={required}
        needsLine={
          showErrors && missingKeys.length > 0
            ? registrationNeedsLine(missingKeys)
            : null
        }
      />
    </>
  );
}
